import React, { useEffect, useState, useMemo } from "react";

import { exportToCanvas } from "@excalidraw/excalidraw";

import { pointFrom, pointRotateRads } from "@excalidraw/math";

import type {
  ExcalidrawElement,
  NonDeletedExcalidrawElement,
} from "@excalidraw/element/types";

import {
  useExcalidrawElements,
  useExcalidrawAPI,
} from "../../packages/excalidraw/components/App";

interface MiniatureData {
  id: string;
  dataUrl: string;
  element: ExcalidrawElement;
  width: number;
  height: number;
  formato: string;
}

export const MiniaturePanel: React.FC = () => {
  const elements = useExcalidrawElements();
  const api = useExcalidrawAPI();

  const [miniatures, setMiniatures] = useState<MiniatureData[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [panelOrientation, setPanelOrientation] = useState<
    "portrait" | "landscape"
  >("portrait");

  // Isola os papéis (sheets)
  const papers = useMemo(() => {
    return elements.filter((el) => el.customData?.isPapel && !el.isDeleted);
  }, [elements]);

  useEffect(() => {
    let active = true;

    const generateThumbnails = async () => {
      if (!api) {
        return;
      }
      if (papers.length === 0) {
        setMiniatures([]);
        return;
      }

      setIsGenerating(true);

      const files = api.getFiles();
      const newMiniatures: MiniatureData[] = [];
      const elementsMap = new Map<string, ExcalidrawElement>();
      elements.forEach((el) => elementsMap.set(el.id, el));

      for (const papel of papers) {
        // Otimização: Apenas renderiza na miniatura o que está agrupado intencionalmente com o papel
        const intersectingElements = elements.filter((el) => {
          if (el.isDeleted) {
            return false;
          }
          if (el.id === papel.id) {
            return true;
          } // Inclui o próprio papel

          // Verifica se compartilham algum groupId
          if (
            el.groupIds &&
            papel.groupIds &&
            papel.groupIds.length > 0 &&
            el.groupIds.length > 0
          ) {
            return el.groupIds.some((id) => papel.groupIds.includes(id));
          }

          return false;
        });

        // Un-rotate os elementos para que a miniatura seja sempre 0 graus
        const cx = papel.x + papel.width / 2;
        const cy = papel.y + papel.height / 2;

        const isNaturallyPortrait = papel.width < papel.height;
        const needsExtraRotation =
          (panelOrientation === "landscape" && isNaturallyPortrait) ||
          (panelOrientation === "portrait" && !isNaturallyPortrait);
        const extraAngle = needsExtraRotation ? Math.PI / 2 : 0;
        const totalAngleOffset = -papel.angle + extraAngle;

        const unrotatedElements = intersectingElements.map((el) => {
          if (totalAngleOffset === 0 && el.angle === 0 && !needsExtraRotation) {
            return el;
          }

          const elCx = el.x + el.width / 2;
          const elCy = el.y + el.height / 2;

          const [newElCx, newElCy] = pointRotateRads(
            pointFrom(elCx, elCy),
            pointFrom(cx, cy),
            totalAngleOffset as any,
          );

          return {
            ...el,
            x: newElCx - el.width / 2,
            y: newElCy - el.height / 2,
            angle: (el.angle + totalAngleOffset) as any,
          };
        }) as unknown as readonly NonDeletedExcalidrawElement[];

        const unrotatedPapel = unrotatedElements.find(
          (el) => el.id === papel.id,
        );

        try {
          // Usa o export nativo para desenhar a imagem (usando canvas para performance e base64 rápido)
          const currentAppState = api.getAppState();
          const canvas = await exportToCanvas({
            elements: unrotatedElements,
            appState: {
              ...currentAppState,
              exportBackground: false,
              viewBackgroundColor: "#ffffff",
            },
            files,
            exportPadding: 0,
            exportingFrame: unrotatedPapel as any,
          });

          if (active) {
            newMiniatures.push({
              id: papel.id,
              dataUrl: canvas.toDataURL("image/webp", 0.5), // compressão leve para não estourar memória
              element: papel,
              width: papel.width,
              height: papel.height,
              formato: papel.customData?.formato || "Folha",
            });
          }
        } catch (error) {
          console.error("Erro gerando miniatura para", papel.id, error);
        }
      }

      if (active) {
        setMiniatures(newMiniatures);
        setIsGenerating(false);
      }
    };

    // Debounce generation by 1 second to avoid killing the UI while drawing
    const timeoutId = setTimeout(generateThumbnails, 1000);

    return () => {
      active = false;
      clearTimeout(timeoutId);
    };
  }, [papers, elements, api, panelOrientation]);

  const handleMiniatureClick = (papel: ExcalidrawElement) => {
    if (!api) {
      return;
    }
    api.scrollToContent(papel, { fitToContent: true });
  };

  return (
    <div
      className="onepaper-miniature-panel"
      style={{
        position: "absolute",
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
      }}
    >
      <style>{`
        .custom-miniature-scroll::-webkit-scrollbar {
          width: 8px;
        }
        .custom-miniature-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-miniature-scroll::-webkit-scrollbar-thumb {
          background-color: #000;
          border-radius: 4px;
        }
      `}</style>

      <div
        style={{
          padding: "1rem 1rem 0 1rem",
          flexShrink: 0,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "1rem",
        }}
      >
        <h2
          style={{
            fontSize: "1.2rem",
            fontWeight: "bold",
            margin: 0,
            color: "var(--text-color-primary)",
          }}
        >
          Navegação
        </h2>
        <div style={{ display: "flex", gap: "0.2rem" }}>
          <button
            onClick={() => setPanelOrientation("portrait")}
            style={{
              background:
                panelOrientation === "portrait"
                  ? "var(--button-hover-bg)"
                  : "transparent",
              border: "1px solid var(--color-gray-30)",
              borderRadius: "4px",
              padding: "0.2rem 0.4rem",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--text-color-primary)",
            }}
            title="Retrato"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M6 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6H6z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
            </svg>
          </button>
          <button
            onClick={() => setPanelOrientation("landscape")}
            style={{
              background:
                panelOrientation === "landscape"
                  ? "var(--button-hover-bg)"
                  : "transparent",
              border: "1px solid var(--color-gray-30)",
              borderRadius: "4px",
              padding: "0.2rem 0.4rem",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--text-color-primary)",
            }}
            title="Paisagem"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 8a2 2 0 0 1 2-2h10l4 4v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8z"></path>
              <polyline points="16 6 16 10 20 10"></polyline>
            </svg>
          </button>
        </div>
      </div>

      <div
        className="custom-miniature-scroll"
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "0 1rem 1rem 1rem",
          boxSizing: "border-box",
        }}
      >
        {papers.length === 0 ? (
          <div
            style={{
              color: "var(--text-color-primary)",
              fontSize: "0.9rem",
              textAlign: "center",
              marginTop: "2rem",
            }}
          >
            Nenhuma folha adicionada.
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.5rem",
              paddingBottom: "1rem",
            }}
          >
            {miniatures.map((min, index) => (
              <div
                key={min.id}
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  cursor: "pointer",
                  padding: "0.25rem 0.5rem",
                  transition: "background 0.2s",
                  backgroundColor: "transparent",
                  borderRadius: "8px",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor =
                    "var(--button-hover-bg)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
                onClick={() => handleMiniatureClick(min.element)}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    justifyContent: "center",
                    width: "30px",
                    marginRight: "0.5rem",
                    flexShrink: 0,
                  }}
                >
                  <span
                    style={{
                      fontWeight: "bold",
                      fontSize: "0.9rem",
                      color: "var(--text-color-primary)",
                    }}
                  >
                    {index + 1}
                  </span>
                  <span
                    style={{
                      fontSize: "0.65rem",
                      color: "var(--text-color-primary)",
                      opacity: 0.7,
                    }}
                  >
                    {min.formato}
                  </span>
                </div>

                <img
                  src={min.dataUrl}
                  alt={`Miniatura Folha ${index + 1}`}
                  style={{
                    flex: 1,
                    maxWidth: "160px",
                    width: "60%",
                    objectFit: "contain",
                    border: "1px solid var(--color-gray-30)",
                    background: "#fff",
                    display: "block",
                    borderRadius: "4px",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                />
              </div>
            ))}
            {isGenerating && miniatures.length < papers.length && (
              <div
                style={{
                  color: "var(--text-color-primary)",
                  fontSize: "0.8rem",
                  textAlign: "center",
                  opacity: 0.6,
                  marginTop: "1rem",
                }}
              >
                Gerando miniaturas...
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
